import { createBrowserRouter, Navigate } from 'react-router-dom'
import { ProtectedRoute } from '../shared/components/ProtectedRoute'
import { CrmRoleGate } from '../shared/components/CrmRoleGate'
import { Layout } from '../shared/components/Layout'
import { LoginPage } from '../modules/auth/LoginPage'
import { DashboardPage } from '../modules/dashboard/DashboardPage'
import { RosDashboardPage } from '../modules/dashboard/RosDashboardPage'
import { ClientsPage } from '../modules/clients/ClientsPage'
import { ClientDetailPage } from '../modules/clients/ClientDetailPage'
import { ClientProjectsPage } from '../modules/clients/ClientProjectsPage'
import { NewClientPage } from '../modules/clients/NewClientPage'
import { RequestsPage } from '../modules/requests/RequestsPage'
import { NewRequestPage } from '../modules/requests/NewRequestPage'
import { RequestDetailPage } from '../modules/requests/RequestDetailPage'
import { RequestHistoryPage } from '../modules/requests/RequestHistoryPage'
import { QuotesSelectionPage } from '../modules/requests/quotes-selection/QuotesSelectionPage'
import { QuotesOverviewPage } from '../modules/requests/quotes-overview/QuotesOverviewPage'
import { QueueRouteGuard } from '../modules/procurement/QueueRouteGuard'
import { ProcurementRequestsPage } from '../modules/procurement-requests/ProcurementRequestsPage'
import { ProcurementRequestDetailPage } from '../modules/procurement-requests/ProcurementRequestDetailPage'
import { RequestItemPage } from '../modules/request-items/RequestItemPage'
import { ItemThreadPage } from '../modules/request-items/ItemThreadPage'
import { TablePage } from '../modules/table/TablePage'
import { AccountSettingsPage } from '../modules/auth/AccountSettingsPage'
import { AccountsPage } from '../modules/admin/AccountsPage'
import { PartCatalogPage } from '../modules/admin/PartCatalogPage'
import { BrandsPage } from '../modules/brands/BrandsPage'
import { ExchangeRatesPage } from '../modules/exchange-rates/ExchangeRatesPage'
import { NomenclatureRefPage } from '../modules/nomenclature-ref/NomenclatureRefPage'
import { FeedbackPage } from '../modules/feedback/FeedbackPage'
import { DatabasePage } from '../modules/database/DatabasePage'
import { KnowledgeBasePage } from '../modules/knowledge-base/KnowledgeBasePage'
import { NotificationsPage } from '../modules/notifications/NotificationsPage'
import { NewsPage } from '../modules/news/NewsPage'
import { PlanningPage } from '../modules/planning/PlanningPage'
import { AnalyticsPage } from '../modules/analytics/AnalyticsPage'
import { OrdersPage } from '../modules/orders/OrdersPage'
import { OrderDetailPage } from '../modules/orders/OrderDetailPage'
import CatalogPage from '../modules/catalog/CatalogPage'
import SuppliersPage from '../modules/suppliers/SuppliersPage'
import SupplierDetailPage from '../modules/suppliers/SupplierDetailPage'
import PartCardPage from '../modules/catalog/PartCardPage'
import { CustomsRouteGate } from '../modules/customs/CustomsRouteGate'
import { CustomsQueuePage } from '../modules/customs/CustomsQueuePage'
import { BrokerRequestsPage } from '../modules/customs/broker/BrokerRequestsPage'
import { BrokerRequestDetailPage } from '../modules/customs/broker/BrokerRequestDetailPage'
import { BrokersPage } from '../modules/customs/brokers/BrokersPage'
import { BankRouteGate } from '../modules/bank/BankRouteGate'
import { BankPage } from '../modules/bank/BankPage'
import { BankStatementPage } from '../modules/bank/BankStatementPage'
import { NotFoundPage } from '../shared/components/NotFoundPage'

const ACCOUNTANT_SECTIONS_ROLES = ['ACCOUNTANT'] as const

export const router = createBrowserRouter(
  [
    {
      path: '/login',
      element: <LoginPage />,
    },
    {
      path: '/',
      element: (
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      ),
      children: [
        { index: true, element: <Navigate to="/dashboard" replace /> },
        { path: 'dashboard', element: <DashboardPage /> },
        // Разделы, открытые бухгалтеру: роли CRM + ACCOUNTANT. C2 — «Планирование» и
        // «Курс валют»; Б1 (11.10.2026) — «Заказы» на просмотр и «Клиенты»; с 14.10.2026
        // бухгалтер создаёт и правит клиентов («Проекты» клиента — за общими воротами ниже).
        {
          element: <CrmRoleGate extraRoles={ACCOUNTANT_SECTIONS_ROLES} />,
          children: [
            { path: 'planning', element: <PlanningPage /> },
            { path: 'exchange-rates', element: <ExchangeRatesPage /> },
            { path: 'clients', element: <ClientsPage /> },
            { path: 'clients/new', element: <NewClientPage /> },
            { path: 'clients/:id', element: <ClientDetailPage /> },
            { path: 'orders', element: <OrdersPage /> },
            { path: 'orders/:id', element: <OrderDetailPage /> },
            // Справочник номенклатуры УНФ — бухгалтер загружает выгрузку из УНФ (15.10.2026).
            { path: 'unf-nomenclature', element: <NomenclatureRefPage /> },
          ],
        },
        // Бизнес-разделы — только роли CRM до этапа 3 (CrmRoleGate). Вне ворот —
        // главная, настройки, уведомления, новости и обратная связь (самообслуживание).
        {
          element: <CrmRoleGate />,
          children: [
            { path: 'dashboard/ros', element: <RosDashboardPage /> },
            { path: 'analytics', element: <AnalyticsPage /> },
            { path: 'clients/:id/projects', element: <ClientProjectsPage /> },
            { path: 'requests', element: <RequestsPage /> },
            { path: 'requests/new', element: <NewRequestPage /> },
            // Алиас для старых закладок и пункта меню «Разобрать заявку».
            // Функционал «Разобрать заявку» объединён с «Новая заявка» в NewRequestPage.
            { path: 'requests/parse', element: <NewRequestPage /> },
            { path: 'requests/:id', element: <RequestDetailPage /> },
            { path: 'requests/:id/history', element: <RequestHistoryPage /> },
            { path: 'requests/:id/quotes-selection', element: <QuotesSelectionPage /> },
            { path: 'requests/:id/quotes-overview', element: <QuotesOverviewPage /> },
            { path: 'procurement/queue', element: <QueueRouteGuard /> },
            { path: 'procurement-requests', element: <ProcurementRequestsPage /> },
            { path: 'procurement-requests/:id', element: <ProcurementRequestDetailPage /> },
            { path: 'request-items/:id', element: <RequestItemPage /> },
            { path: 'table', element: <TablePage /> },
            { path: 'catalog', element: <CatalogPage /> },
            { path: 'suppliers', element: <SuppliersPage /> },
            { path: 'suppliers/:id', element: <SupplierDetailPage /> },
            { path: 'catalog/part/:artKey', element: <PartCardPage /> },
            { path: 'accounts', element: <AccountsPage /> },
            { path: 'admin/part-catalog', element: <PartCatalogPage /> },
            { path: 'brands', element: <BrandsPage /> },
            { path: 'database', element: <DatabasePage /> },
            { path: 'knowledge-base', element: <KnowledgeBasePage /> },
          ],
        },
        // «Банк» (бухгалтер, Б2): свои ворота вне CrmRoleGate — бухгалтер, ADMIN, ГД.
        {
          element: <BankRouteGate />,
          children: [
            { path: 'bank', element: <BankPage /> },
            { path: 'bank/statements/:id', element: <BankStatementPage /> },
          ],
        },
        // «Нетарифка» (логист, Л1–Л3): свои ворота вне CrmRoleGate — логист (роль
        // этапа 3), ADMIN, ГД и РОЗ; менеджеру, закупщику, РОП и помощнику закрыто.
        {
          element: <CustomsRouteGate />,
          children: [
            { path: 'customs', element: <CustomsQueuePage /> },
            { path: 'customs/requests', element: <BrokerRequestsPage /> },
            { path: 'customs/requests/:id', element: <BrokerRequestDetailPage /> },
            { path: 'customs/brokers', element: <BrokersPage /> },
          ],
        },
        // «Переписка по позиции» — вне ворот: сюда ведут уведомления бухгалтеру,
        // складу и логисту, которых @упомянули; доступ проверяет сервер (21.10.2026).
        { path: 'item-comments/:itemId', element: <ItemThreadPage /> },
        { path: 'settings', element: <AccountSettingsPage /> },
        { path: 'feedback', element: <FeedbackPage /> },
        { path: 'notifications', element: <NotificationsPage /> },
        { path: 'news', element: <NewsPage /> },
        // Catch-all внутри авторизованной зоны: 404 в контексте CRM (sidebar + валюты).
        { path: '*', element: <NotFoundPage /> },
      ],
    },
    // Catch-all вне Layout: для неаутентифицированных ProtectedRoute
    // редиректит на /login, поэтому сюда практически не попадаем.
    // Оставлено как safety-net, если кто-то обойдёт ProtectedRoute.
    { path: '*', element: <Navigate to="/" replace /> },
  ],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { future: { v7_startTransition: true } as any },
)
